import { useActionFailedAlert } from 'shared/problems/composables/actionFailedAlert';
import type { ProblemDetails } from 'shared/problems/domain/problemDetails';
import { Assert } from 'shared/assert';
import {
  useInventoryStorageArea,
  type InventoryStorageArea,
} from '@/storageAreas/api/inventoryStorageArea';
import { StorageAreaType } from 'shared/storageAreas/domain/storageAreaType';
import { defineStore, storeToRefs } from 'pinia';
import { ref, shallowRef, watch } from 'vue';
import { useUndoStore } from 'shared/undo/stores/undoStore';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { useUndoInventoryApi } from '@/storageAreas/api/undoInventoryApi';
import { ProductType } from 'shared/products/domain/productType';
import { useProductStore } from 'shared/products/stores/productStore';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import type { BulkStorageData } from 'shared/storageAreas/domain/storageArea';
import { convertToDisplayUnit, convertToTon, Unit } from 'shared/common/domain/bulkUnits';

export interface ShowDialog {
  storageAreaId: string;
  productId?: string;
}

export interface ProductContent {
  product: Product;
  ton: number;
  shouldSave: boolean;
}

export interface Product {
  id: string;
  name: string;
}

export const useEditBulkInventoryStore = defineStore('editBulkInventory', () => {
  const storageAreaStore = useStorageAreaStore();
  const productStore = useProductStore();
  const inventoryStorageArea = useInventoryStorageArea();
  const undoStore = useUndoStore();
  const languageStore = useLanguageStore();
  const { translations } = storeToRefs(languageStore);
  const undoInventoryApi = useUndoInventoryApi();
  const refreshProductStore = useRefereshProductStore();

  const visible = ref<boolean>(false);
  const storageAreaId = ref<string>();
  const storageAreaType = ref<StorageAreaType>(StorageAreaType.Undefined);
  const storageAreaName = ref<string>();
  const bulkStorage = ref<BulkStorageData | null>(null);
  const selectedProductId = ref<string | null>(null);
  const selectedProductName = ref<string | null>(null);
  const selectedProductUnit = ref<Unit | null>(null);
  const currentValue = ref<number>(0);
  const currentUnit = ref<number>(0);
  const newValue = ref<number>(0);
  const productOptions = shallowRef<Product[]>([]);
  const contentByProduct = new Map<string, ProductContent>();
  const problems = ref<ProblemDetails>();
  const actionFailedAlert = useActionFailedAlert();

  watch(selectedProductId, (productId) => {
    productSelected(productId);
  });

  const saving = ref(false);

  async function show(options: ShowDialog) {
    selectedProductId.value = null;
    selectedProductName.value = null;
    currentValue.value = 0;
    currentUnit.value = 0;
    newValue.value = 0;
    problems.value = undefined;
    productOptions.value = [];
    contentByProduct.clear();

    const storageArea = storageAreaStore.getById(options.storageAreaId);

    Assert.definied(storageArea);

    const bulkStorageData = storageArea.bulkStorageData;
    Assert.truthy(
      bulkStorageData,
      "Can't edit bulk inventory on a storage area that is not a bulk storage!",
    );

    bulkStorage.value = bulkStorageData;
    storageAreaId.value = storageArea.id;
    storageAreaType.value = storageArea.type;
    storageAreaName.value = storageArea.name;

    await refreshProductStore.initialize();
    for (const productContent of bulkStorageData.content) {
      const product = productStore.getById(productContent.productId);
      if (!product) continue;
      contentByProduct.set(product.id, {
        product: product,
        ton: productContent.ton,
        shouldSave: true,
      });
    }

    if (bulkStorageData.allowedProducts.length === 0) {
      for (const product of productStore.products) {
        if (product.type !== ProductType.Bulk) continue;
        if (contentByProduct.has(product.id)) continue;
        contentByProduct.set(product.id, {
          product,
          ton: 0,
          shouldSave: false,
        });
      }
    } else {
      for (const productId of bulkStorageData.allowedProducts) {
        const product = productStore.getById(productId);
        if (!product) continue;
        if (contentByProduct.has(product.id)) continue;
        contentByProduct.set(product.id, {
          product,
          ton: 0,
          shouldSave: false,
        });
      }
    }

    const products = Array.from(contentByProduct.values()).map((c) => c.product);
    products.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        numeric: true,
      }),
    );

    productOptions.value = products;

    const productId = options.productId;

    if (productId !== undefined) {
      selectProduct(productId);
    } else if (products.length === 1) {
      const productId = products[0]!.id;
      selectProduct(productId);
    } else if (contentByProduct.size > 0) {
      const content = Array.from(contentByProduct.values()).sort(
        (a, b) => b.ton - a.ton,
      )[0];
      if (content && content.ton > 0) {
        selectProduct(content.product.id);
      }
    }

    visible.value = true;
  }

  function selectProduct(productId: string) {
    selectedProductId.value = productId;
    productSelected(productId);
  }

  function productSelected(productId: string | null) {
    if (!productId) {
      currentValue.value = 0;
      currentUnit.value = 0;
      newValue.value = 0;
      selectedProductName.value = null;
      return;
    }

    const product = productStore.getById(productId);
    Assert.definied(product, 'Product not found, this should not be possible');
    Assert.definied(product.bulkData, 'Missing Bulkdata on product');

    selectedProductName.value = product.name;
    selectedProductUnit.value = product.bulkData.unit;

    const content = contentByProduct.get(productId);
    Assert.truthy(content, 'Product content not found, this should not be possible!');

    currentValue.value = convertToDisplayUnit(content.ton, product.bulkData.unit, product.bulkData.density);
    newValue.value = convertToDisplayUnit(content.ton, product.bulkData.unit, product.bulkData.density);
  }

  async function save() {
    const productId = selectedProductId.value;

    Assert.truthy(productId, 'Product missing!');
    Assert.truthy(storageAreaId.value, 'Storage area missing!');

    try {
      saving.value = true;

      const product = productStore.getById(productId);

      Assert.truthy(product, 'Bulkdata is missing!');
      Assert.truthy(product?.bulkData, 'Bulkdata is missing!');

      const request: InventoryStorageArea = {
        storageAreaId: storageAreaId.value,
        storageAreaType: storageAreaType.value,
        bulkStorageData: [
          {
            ton: convertToTon(newValue.value, product.bulkData.unit, product.bulkData.density),
            productId: productId,
          },
        ],
      };

      Assert.truthy(request.bulkStorageData, 'Bulk storage data is missing, this should not be possible');
      for (const content of contentByProduct.values()) {
        if (content.product.id === productId || !content.shouldSave) continue;
        request.bulkStorageData.push({
          ton: content.ton,
          productId: content.product.id,
        });
      }

      const totalValue = request.bulkStorageData
        .map((c) => c.ton)
        .reduce((a, b) => a + b);

      const response = await inventoryStorageArea.execute(request);

      if (!response.ok) {
        problems.value = response.details;
        return;
      }

      if (totalValue === 0) {
        undoStore.add({
          title: translations.value.undo.inventoryTitle,
          description: translations.value.undo.getUndoBulkInventoryDescription(
            product?.name ?? 'Unknown',
            currentValue.value,
            currentUnit.value,
            storageAreaName.value ?? 'Unknown',
          ),
          function: async () => {
            const response = await undoInventoryApi.execute();

            if (!response.ok) {
              actionFailedAlert.show(response.details);
              return;
            }

            storageAreaStore.add(response.storageArea);
          },
        });
      } else {
        const undoRequest: InventoryStorageArea = {
          storageAreaId: storageAreaId.value,
          storageAreaType: storageAreaType.value,
          bulkStorageData: [
            {
              ton: currentValue.value,
              productId: productId,

            },
          ],
          undo: true,
        };

        for (const content of contentByProduct.values()) {
          if (content.product.id === productId) continue;
          request.bulkStorageData.push({
            ton: content.ton,
            productId: content.product.id,
          });
        }

        undoStore.add({
          title: translations.value.undo.inventoryTitle,
          description: translations.value.undo.getUndoBulkInventoryDescription(
            product?.name ?? 'Unknown',
            currentValue.value,
            currentUnit.value,
            storageAreaName.value ?? 'Unknown',
          ),
          function: async () => {
            const response = await inventoryStorageArea.execute(undoRequest);

            if (!response.ok) {
              actionFailedAlert.show(response.details);
              return;
            }

            storageAreaStore.add(response.storageArea);
          },
        });
      }

      storageAreaStore.add(response.storageArea);
      storageAreaId.value = undefined;
      storageAreaType.value = StorageAreaType.Undefined;
      storageAreaName.value = undefined;
      visible.value = false;
    } finally {
      saving.value = false;
    }
  }

  return {
    show,
    save,
    visible,
    problems,
    selectedProductId,
    currentValue,
    newValue,
    productOptions,
    saving,
    bulkStorage,
    storageAreaName,
    selectedProductName,
    selectedProductUnit
  };
});
